import { HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  EmployeeObservationsDto,
  ManageVacationsCriteriaDto,
  SeasonCalendarDto
} from '@app/data/dtos/manage-vacations/manage-vacations.dto';
import { MomoManageVacationsProposalsDto } from '@app/data/dtos/manage-vacations/momo/momo-manage-vacations.dto';
import {
  mapManageVacationsCriteria,
  mapObservations,
  mapSeasonCalendar
} from '@app/data/mappers/manage-vacations/manage-vacations.mapper';
import {
  mapMomoCreateProposalDto,
  mapMomoVacationsProposals
} from '@app/data/mappers/manage-vacations/momo/momo-manage-vacations.mapper';
import { MomoManageVacationsRepository } from '@app/domain/repositories/manage-vacations/momo/momo-manage-vacations.repository.contract';
import { ManageVacationsUserType, SEASONS } from '@app/entities/enums/manage-vacations/manage-vacations.enum';
import { ManageVacationsCriteria } from '@app/entities/models/manage-vacations/manage-vacations-criteria.model';
import { EmployeeObservations, SeasonCalendar } from '@app/entities/models/manage-vacations/manage-vacations.model';
import { MomoManageVacationsProposals } from '@app/entities/models/manage-vacations/momo-manage-vacations.model';
import { TUserTypeCalendarProposal } from '@app/entities/types/manage-vacations.type';
import { ENV } from '@environments/environment';
import {
  IGNORE_NO_CONTENT_ERROR,
  SECTION_KEY
} from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';

import { MRepository } from '@mercadona/core/utils/repository';

@Injectable({
  providedIn: 'root'
})
export class MomoManageVacationsRepositoryImpl extends MRepository implements MomoManageVacationsRepository {
  readonly momoBaseUrl = '/momo/v1/vacations-proposal';

  constructor() {
    super(`${ENV.API_URL}`);
  }

  getProposals(): Observable<MomoManageVacationsProposals> {
    return this.get<MomoManageVacationsProposalsDto>(this.momoBaseUrl, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations').set(IGNORE_NO_CONTENT_ERROR, true)
    }).pipe(map(mapMomoVacationsProposals));
  }

  getVacationsCriteria(): Observable<ManageVacationsCriteria> {
    return this.get<ManageVacationsCriteriaDto>(`${this.momoBaseUrl}/criteria`, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations')
    }).pipe(map(mapManageVacationsCriteria));
  }

  getSeasonCalendar(season: SEASONS): Observable<SeasonCalendar<TUserTypeCalendarProposal>> {
    let params = new HttpParams();
    params = params.set('season', season);

    return this.get<SeasonCalendarDto>(`${this.momoBaseUrl}/season-calendar`, {
      params,
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations')
    }).pipe(map(mapSeasonCalendar(ManageVacationsUserType.MOMO)));
  }

  createProposal(proposals: MomoManageVacationsProposals): Observable<MomoManageVacationsProposals> {
    const createProposal = mapMomoCreateProposalDto(proposals);

    const request = {
      body: {
        ...createProposal
      }
    };

    return this.post<MomoManageVacationsProposalsDto>(this.momoBaseUrl, request).pipe(map(mapMomoVacationsProposals));
  }

  updateObservations(observations: string): Observable<EmployeeObservations> {
    const request = {
      body: {
        content: observations
      }
    };

    return this.put<EmployeeObservationsDto>(`${this.momoBaseUrl}/observations`, request).pipe(map(mapObservations));
  }
}
